
import { Product, UsageRecord } from "@/lib/types";

interface CriticalProductAlertProps {
  groupProducts: Product[];
  usageRecords: UsageRecord[];
  usageTimeRange: "1month" | "3months" | "6months";
}

const CriticalProductAlert = ({
  groupProducts,
  usageRecords,
  usageTimeRange
}: CriticalProductAlertProps) => {
  // For groups, we find the product with the least days remaining (most critical)
  const calculateProductDaysRemaining = (product: Product) => {
    const days = usageTimeRange === "1month" ? 30 : usageTimeRange === "3months" ? 90 : 180;
    
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - days);
    
    const productUsageRecords = usageRecords
      .filter(record => 
        record.productId === product.id && 
        new Date(record.date) >= startDate
      );
    
    if (productUsageRecords.length === 0) return Infinity;
    
    const totalUsage = productUsageRecords.reduce((sum, record) => sum + record.quantity, 0);
    const usageRate = totalUsage / days;
    
    return usageRate > 0 ? Math.floor(product.currentStock / usageRate) : Infinity;
  };
  
  // Find the product with the least days remaining
  const productsWithDaysRemaining = groupProducts.map(product => ({
    product,
    daysRemaining: calculateProductDaysRemaining(product)
  }));
  
  // Sort by days remaining (ascending)
  productsWithDaysRemaining.sort((a, b) => a.daysRemaining - b.daysRemaining);
  
  const mostCriticalProduct = productsWithDaysRemaining.length > 0 ? productsWithDaysRemaining[0] : null;

  if (!mostCriticalProduct) return null;

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md">
      <h3 className="text-md font-semibold mb-2">Critical Product Alert</h3>
      <p className="text-sm text-muted-foreground">
        {mostCriticalProduct.product.name} is the most critical product in this group with only {mostCriticalProduct.daysRemaining} days remaining until restock is needed.
      </p>
    </div>
  );
};

export default CriticalProductAlert;
